import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useTranslation } from 'react-i18next';
import { useLocalParticipant, useRoomContext } from '@livekit/components-react';
import { MediaDeviceFailure, RoomEvent } from 'livekit-client';
import { addWarningBreadcrumb, reportError } from '../../../lib/sentry';
import { isExpectedDeviceFailure } from '../callSignals';
import { DS } from '../designTokens';

type InputKind = 'audioinput' | 'videoinput';

type DeviceFailures = Partial<Record<InputKind, MediaDeviceFailure>>;

const INPUT_KINDS: InputKind[] = ['audioinput', 'videoinput'];

// LiveKit leaves the kind out when it cannot tell which device failed, so
// both count as failed and Retry tries both.
const failedKinds = (kind?: MediaDeviceKind): InputKind[] => {
  if (kind === 'audioinput' || kind === 'videoinput') return [kind];
  if (kind === 'audiooutput') return [];
  return INPUT_KINDS;
};

const messageKey = (failures: DeviceFailures): string => {
  if (Object.values(failures).includes(MediaDeviceFailure.PermissionDenied)) {
    return 'videoVisit.error.permissionsDenied';
  }
  return 'videoVisit.error.mediaDevice';
};

const DeviceFailedBanner = () => {
  const { t } = useTranslation();
  const room = useRoomContext();
  const { isMicrophoneEnabled, isCameraEnabled, localParticipant } =
    useLocalParticipant();
  const [failures, setFailures] = useState<DeviceFailures>({});

  useEffect(() => {
    const onDeviceError = (error: Error, kind?: MediaDeviceKind) => {
      const failure = MediaDeviceFailure.getFailure(error);
      const message = `LiveKit media device failure: ${failure ?? 'unknown'}`;
      const mediaDeviceKind = kind ?? 'unknown';
      if (isExpectedDeviceFailure(failure)) {
        addWarningBreadcrumb('video', `${message} (${mediaDeviceKind})`);
      } else {
        reportError(new Error(message), {
          tags: { feature: 'video', mediaDeviceKind },
        });
      }
      // The clinician stays in the room without the device and can turn it
      // on from here once the browser allows it.
      setFailures((current) => {
        const next = { ...current };
        failedKinds(kind).forEach((failedKind) => {
          next[failedKind] = failure;
        });
        return next;
      });
    };
    room.on(RoomEvent.MediaDevicesError, onDeviceError);
    return () => {
      room.off(RoomEvent.MediaDevicesError, onDeviceError);
    };
  }, [room]);

  useEffect(() => {
    setFailures((current) => {
      const next = { ...current };
      if (isMicrophoneEnabled) delete next.audioinput;
      if (isCameraEnabled) delete next.videoinput;
      return next;
    });
  }, [isMicrophoneEnabled, isCameraEnabled]);

  if (Object.keys(failures).length === 0) return null;

  // Once the browser blocks a device it does not ask again, so this only
  // succeeds after the clinician has allowed it in the address bar.
  const retry = () => {
    const attempts: Promise<unknown>[] = [];
    if ('audioinput' in failures) {
      attempts.push(localParticipant.setMicrophoneEnabled(true));
    }
    if ('videoinput' in failures) {
      attempts.push(localParticipant.setCameraEnabled(true));
    }
    Promise.all(attempts).catch(() => {
      // The room emits MediaDevicesError again, which records it and keeps
      // this banner up.
    });
  };

  return (
    <Box
      role="alert"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderRadius: '16px',
        bgcolor: DS.dawnSurface,
        color: DS.softBlack,
      }}
    >
      <ErrorOutlineIcon sx={{ color: DS.softBlack }} />
      <Typography sx={{ flex: 1, fontFamily: DS.fontBody, fontSize: 14 }}>
        {t(messageKey(failures))}
      </Typography>
      <Button
        onClick={retry}
        sx={{
          minHeight: 44,
          px: 2,
          textTransform: 'none',
          fontFamily: DS.fontBody,
          fontWeight: 700,
          borderRadius: '999px',
          bgcolor: DS.deepTeal,
          color: DS.linen,
          '&:hover': { bgcolor: DS.deepTeal, filter: 'brightness(0.96)' },
        }}
      >
        {t('videoVisit.retry')}
      </Button>
    </Box>
  );
};

export default DeviceFailedBanner;
